import React, { ReactNode, useContext, useEffect, useState } from 'react';

import UbiquitiHostFirewallZonesContext from '@totem/components/ubiquiti/hosts/details/firewallZones/ubiquitiHostFirewallZonesContext';
import {
  UbiquitiApiDataItem,
  UbiquitiFirewallZone,
} from '@totem/components/ubiquiti/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { UBIQUITI_HOSTS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  hostId: string;
  children?: ReactNode;
};

// Standalone by design (takes hostId as a prop, not from route params) so it
// can be embedded anywhere a host's firewall zone list is needed, not just
// the details drill-down page.
const UbiquitiHostFirewallZonesContainer = ({ hostId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [firewallZones, setFirewallZones] = useState<
    UbiquitiApiDataItem<UbiquitiFirewallZone>[]
  >([]);

  useEffect(() => {
    setLoading(true);

    setError(false);

    // Host IDs contain a literal colon (e.g. "D8B3...:1815601434"), which is
    // legal in a URL path segment per RFC 3986 but not guaranteed to survive
    // every intermediate proxy/gateway unencoded — encode defensively.
    fetch(
      `${UBIQUITI_HOSTS_ENDPOINT}/${encodeURIComponent(hostId)}/firewallZones`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => {
        // A non-2xx response body is an APIErrorResponse object, not a
        // firewall zone array — never hand that to the table as dataSource.
        if (!res.ok) {
          setError(true);
          return [];
        }
        return res.json();
      })
      .then((result: UbiquitiApiDataItem<UbiquitiFirewallZone>[]) => {
        setFirewallZones(Array.isArray(result) ? result : []);
      })
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setError(true);

        setFirewallZones([]);

        setLoading(false);
      });
  }, [hostId, getApiToken]);

  return (
    <UbiquitiHostFirewallZonesContext.Provider
      value={{ loading, error, firewallZones }}
    >
      <div>{children}</div>
    </UbiquitiHostFirewallZonesContext.Provider>
  );
};

export default UbiquitiHostFirewallZonesContainer;
